// SPDX-License-Identifier: Apache-2.0

// VaultTopbar — dense HUD for the vault surface. Solid port of v1's
// `vault-topbar.tsx` (apps/web/src/components/vault/vault-topbar.tsx).
//
// Purely presentational: every piece of state lives in the parent and is
// passed down via signal accessors + change callbacks. The parent decides
// what each toggle actually does — this component just renders the chrome
// and emits intent.
//
// Surface (matches the brief in the spawn prompt):
//   - Search input (controlled via `query` accessor + `onQueryChange`)
//   - Layout-mode toggle: ring | cluster
//   - Semantic-edges toggle: off | tfidf | embeddings + progress chip
//   - Stats chips: docs, projects, sessions, cost, needsInput
//   - Embodied-mode toggle (flips a prop the EmbodiedRig listens to)
//
// Coordination notes (per the spawn brief):
//   - The semantic-edges three-state cycle is owned here as a pure
//     presentational concern; the actual edge computation lives in
//     `use-semantic-edges.ts` (Glaucus's hook). We just emit the next state.
//   - We do NOT touch VaultExperience.tsx or VaultOrbit.tsx — those are
//     Hephaestus / shared territory.
//
// The brand-mark and "back to landing" link are deliberately NOT here. Those
// live in the app-shell `lens-tabs` row above the route content. This bar is
// the second strip — the in-route HUD.

import { Show, type Accessor, type JSX } from "solid-js";
import "./VaultTopbar.css";

export interface VaultStats {
  docs: number;
  projects: number;
  sessions: number;
  /** USD spent (e.g. via brain-agent). 0 if unknown / endpoint missing. */
  cost: number;
  /** True when the brain agent is awaiting human input (a permission, etc.). */
  needsInput: boolean;
  /** True while a drag-drop / picker batch is being parsed — disables the
   *  add-file button. Optional so callers don't have to plumb it. */
  ingesting?: boolean;
}

export interface VaultTopbarProps {
  query: Accessor<string>;
  onQueryChange: (q: string) => void;
  layoutMode: Accessor<"ring" | "cluster">;
  onLayoutModeChange: (m: "ring" | "cluster") => void;
  semanticMode: Accessor<"off" | "tfidf" | "embeddings">;
  onSemanticModeChange: (m: "off" | "tfidf" | "embeddings") => void;
  /** 0..1 — only meaningful while semanticMode is `embeddings` and the model
   *  is still loading. Display as a thin progress strip on the chip. */
  semanticProgress: Accessor<number>;
  embodiedMode: Accessor<boolean>;
  onEmbodiedToggle: () => void;
  stats: Accessor<VaultStats>;
  /** Open the hidden file-input picker (single/multi file ingest). */
  onAddFile?: () => void;
  /** Open the FSAccess directory picker. */
  onPickFolder?: () => void;
  /** Optional collab presence slot. Caller renders <PresenceBar /> here.
   *  Iris 3 mounts this from VaultExperience when `roomId` is set. */
  presenceSlot?: JSX.Element;
}

export function VaultTopbar(props: VaultTopbarProps) {
  // ── three-state semantic cycle ───────────────────────────────────────────
  function nextSemantic(): "off" | "tfidf" | "embeddings" {
    const cur = props.semanticMode();
    if (cur === "off") return "tfidf";
    if (cur === "tfidf") return "embeddings";
    return "off";
  }

  function semanticLabel(): string {
    const m = props.semanticMode();
    if (m === "off") return "edges";
    if (m === "tfidf") return "edges · tf-idf";
    return "edges · ai";
  }

  function semanticPressed(): boolean {
    return props.semanticMode() !== "off";
  }

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div
      class="mv-vault-topbar vault-topbar"
      data-testid="vault-topbar"
    >
      {/* Brand badge + count */}
      <div class="mv-vt__brand" aria-hidden="false">
        <span class="mv-vt__brand-logo" aria-hidden="true">◉</span>
        <span class="mv-vt__brand-count" data-testid="vt-brand-count">
          {props.stats().docs.toLocaleString()} docs
        </span>
      </div>

      {/* Collab presence — only present when the parent passes it (roomId set). */}
      <Show when={props.presenceSlot}>
        <div class="mv-vt__presence" data-testid="vt-presence-slot">
          {props.presenceSlot}
        </div>
      </Show>

      {/* Search */}
      <div class="mv-vt__search">
        <span class="mv-vt__search-icon" aria-hidden="true">
          ⌕
        </span>
        <input
          type="search"
          class="mv-vt__search-input"
          value={props.query()}
          placeholder="search the vault…"
          spellcheck={false}
          autocomplete="off"
          onInput={(e) =>
            props.onQueryChange((e.currentTarget as HTMLInputElement).value)
          }
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              props.onQueryChange("");
            }
          }}
          data-testid="vault-topbar-search"
        />
      </div>

      {/* Stats chips */}
      <div class="mv-vt__stats" role="status" aria-label="vault stats">
        <StatChip
          testId="vt-stat-docs"
          label="docs"
          value={props.stats().docs.toLocaleString()}
        />
        <StatChip
          testId="vt-stat-projects"
          label="projects"
          value={props.stats().projects.toLocaleString()}
        />
        <StatChip
          testId="vt-stat-sessions"
          label="sessions"
          value={props.stats().sessions.toLocaleString()}
        />
        <StatChip
          testId="vt-stat-cost"
          label="cost"
          value={`$${props.stats().cost.toFixed(2)}`}
          tone={props.stats().cost > 0 ? "amber" : "muted"}
        />
        {props.stats().needsInput ? (
          <span class="mv-vt__chip mv-vt__chip--alert" data-testid="vt-needs-input">
            <span class="mv-vt__dot mv-vt__dot--alert" />
            needs input
          </span>
        ) : null}
      </div>

      <div class="mv-vt__spacer" />

      {/* Toggles */}
      <button
        type="button"
        class="mv-vt__btn"
        classList={{ "is-pressed": semanticPressed() }}
        onClick={() => props.onSemanticModeChange(nextSemantic())}
        title="cycle: off → tf-idf → embeddings"
        data-testid="vault-topbar-semantic"
        data-mode={props.semanticMode()}
      >
        <span class="mv-vt__btn-icon" aria-hidden="true">
          ✦
        </span>
        {semanticLabel()}
        {props.semanticMode() === "embeddings" &&
        props.semanticProgress() > 0 &&
        props.semanticProgress() < 1 ? (
          <span class="mv-vt__chip-mini" aria-label="model loading">
            {Math.round(props.semanticProgress() * 100)}%
            <span
              class="mv-vt__progress"
              style={{ width: `${Math.round(props.semanticProgress() * 100)}%` }}
            />
          </span>
        ) : null}
      </button>

      <button
        type="button"
        class="mv-vt__btn"
        classList={{ "is-pressed": props.layoutMode() === "cluster" }}
        onClick={() =>
          props.onLayoutModeChange(
            props.layoutMode() === "ring" ? "cluster" : "ring",
          )
        }
        title={
          props.layoutMode() === "cluster"
            ? "switch to ring layout"
            : "switch to cluster layout"
        }
        data-testid="vault-topbar-layout"
        data-layout={props.layoutMode()}
      >
        <span class="mv-vt__btn-icon" aria-hidden="true">
          ◇
        </span>
        {props.layoutMode() === "cluster" ? "cluster" : "ring"}
      </button>

      <button
        type="button"
        class="mv-vt__btn"
        classList={{ "is-pressed": props.embodiedMode() }}
        onClick={() => props.onEmbodiedToggle()}
        title={props.embodiedMode() ? "leave embodied mode" : "enter embodied mode"}
        data-testid="vault-topbar-embodied"
        data-embodied={props.embodiedMode() ? "on" : "off"}
      >
        <span class="mv-vt__btn-icon" aria-hidden="true">
          ◉
        </span>
        embodied
      </button>

      {/* File-ingest actions — keep these as the rightmost group. */}
      {props.onAddFile ? (
        <button
          type="button"
          class="mv-vt__btn"
          onClick={() => props.onAddFile?.()}
          disabled={props.stats().ingesting === true}
          title="add file"
          data-testid="vault-topbar-add-file"
        >
          <span class="mv-vt__btn-icon" aria-hidden="true">
            +
          </span>
          new
        </button>
      ) : null}
      {props.onPickFolder ? (
        <button
          type="button"
          class="mv-vt__btn"
          onClick={() => props.onPickFolder?.()}
          title="pick a folder on disk"
          data-testid="vault-topbar-pick-folder"
        >
          <span class="mv-vt__btn-icon" aria-hidden="true">
            ⌂
          </span>
          pick
        </button>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// StatChip — a single label + value pill. Internal helper.
// ────────────────────────────────────────────────────────────────────────────
interface StatChipProps {
  testId: string;
  label: string;
  value: string;
  tone?: "default" | "muted" | "amber";
}

function StatChip(props: StatChipProps) {
  return (
    <span
      class="mv-vt__chip"
      classList={{
        "mv-vt__chip--muted": props.tone === "muted",
        "mv-vt__chip--amber": props.tone === "amber",
      }}
      data-testid={props.testId}
    >
      <span class="mv-vt__chip-key">{props.label}</span>
      <span class="mv-vt__chip-val">{props.value}</span>
    </span>
  );
}
